import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BACKEND_URL from 'constants/backendUrl';
import DashboardTresholds from 'core/models/dashboardTresholds';
import { GET, POST } from 'utils/http';

// Couche d'accès « état serveur » des seuils du dashboard via TanStack Query.
// Le cache (staleTime) remplace l'ancien flag Redux `alreadyFetched`, et
// l'invalidation de cette clé remplace l'action SetDashboardTresholds + refetch.
// L'état est un objet unique (et non une liste).
export const dashboardTresholdsQueryKey = ['dashboardTresholds'] as const;

interface DashboardTresholdsResponse {
    dashboardSetting: DashboardTresholds;
}

// Endpoint historiquement utilisé par dashboardTresholdsFetcher (DashboardTresholdsProvider).
export const fetchDashboardTresholds = async (): Promise<DashboardTresholds> => {
    const response = (await GET(`${BACKEND_URL}/api/dashboard-setting`)) as DashboardTresholdsResponse;

    return response.dashboardSetting;
};

// Hook de requête de base (la donnée brute du serveur).
export const useDashboardTresholdsQuery = () =>
    useQuery({
        queryKey: dashboardTresholdsQueryKey,
        queryFn: fetchDashboardTresholds,
    });

// Équivalent de dashboardTresholdsSelector(state) -> useDashboardTresholds().
// Renvoie directement la valeur des seuils (ou undefined tant que non chargée).
export const useDashboardTresholds = (): DashboardTresholds | undefined => useDashboardTresholdsQuery().data;

// À appeler après une mutation pour resynchroniser les seuils depuis le serveur.
// Remplace aussi dashboardTresholdsAlreadyFetchedSelector : le cache gère désormais le « déjà chargé ».
export const useInvalidateDashboardTresholds = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: dashboardTresholdsQueryKey });
};

// --- Mutations -----------------------------------------------------------------

interface SetDashboardTresholdsResponse {
    dashboardSetting: DashboardTresholds;
}

// Remplace dispatch(SetDashboardTresholds(...)) + POST /api/dashboard-setting/.
// L'API renvoie les seuils mis à jour ; on rafraîchit le cache avec la réponse
// puis on invalide pour resynchroniser depuis le serveur.
export const useSetDashboardTresholdsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (inputs: DashboardTresholds): Promise<DashboardTresholds> => {
            const response = (await POST<SetDashboardTresholdsResponse>(
                `${BACKEND_URL}/api/dashboard-setting/`,
                inputs as unknown as Record<string, unknown>,
            )) as SetDashboardTresholdsResponse;

            return response.dashboardSetting;
        },
        onSuccess: (dashboardTresholds: DashboardTresholds) => {
            queryClient.setQueryData(dashboardTresholdsQueryKey, dashboardTresholds);

            return queryClient.invalidateQueries({ queryKey: dashboardTresholdsQueryKey });
        },
    });
};
